import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function SProductLookUp({
  handleSelectedProduct,
  formData,
  isShowModal,
}) {
  const dispatch = useDispatch();
  /*   All States
   ********************************************* */
  const [loading, setLoading] = useState(null);
  const [searchText, setSearchText] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [sizesData, setSizesData] = useState([]);

  // Accordion state
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [accordionVariation, setAccordionVariation] = useState(null);
  const [confirming, setConfirming] = useState(false);

  /*   All Functions
   ********************************************* */
  //get Data
  const getProductsData = async () => {
    try {
      setLoading(true);

      let page = 1;
      let limit = 999;
      let query = searchText || null;
      let filterBy = "styleId";

      let body = { page, limit, filterBy };

      if (query) {
        body.query = query;
      }

      const { data } = await API.post("/sales/products", body, {
        headers: authHeader(),
      });
      if (data.success) {
        setProductsData(data.data);
        return data.data;
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  //get Data
  const getSizesData = async () => {
    try {
      setLoading(true);

      let page = 1;
      let limit = 999;
      let query = "";
      let filterBy = "all";

      let body = { page, limit, filterBy };

      if (query) {
        body.query = query;
      }

      const { data } = await API.post("sales/products/sizes", body, {
        headers: authHeader(),
      });
      if (data.success) {
        setSizesData(data.data);

        return data.data;
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Safely parse variations
  const parseVariations = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Safely parse sizes — backend may return a JSON string instead of an array
  const parseSizes = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getProductsWithNames = (product) => {
    const sizes = parseSizes(product.sizes);
    const sizesWithNames = [];
    sizes.forEach((sizeId) => {
      // product.sizes stores `.id` values; compare as strings to avoid type mismatches
      const sizeData = sizesData.find(
        (s) =>
          String(s.id) === String(sizeId) ||
          String(s.sizeId) === String(sizeId),
      );
      if (sizeData) {
        sizesWithNames.push(sizeData.name);
      }
    });

    // sort sizes according to sizesData?.sortId
    sizesWithNames.sort((a, b) => {
      const sizeA = sizesData.find((s) => s.name === a);
      const sizeB = sizesData.find((s) => s.name === b);
      if (sizeA && sizeB) {
        return sizeA.sortId - sizeB.sortId;
      }
      return 0;
    });

    return sizesWithNames;
  };

  const addSizesToProducts = (
    product,
    variation = null,
    skipColorPick = false,
  ) => {
    const sizes = parseSizes(product.sizes);
    const sizesWithNames = getProductsWithNames(product);

    const _updatedProduct = {
      ...product,
      sizes,
      sizesWithNames,
    };

    return handleSelectedProduct(_updatedProduct, variation, skipColorPick);
  };

  const handleToggleAccordion = (productId) => {
    if (expandedProductId === productId) {
      setExpandedProductId(null);
      setAccordionVariation(null);
    } else {
      setExpandedProductId(productId);
      setAccordionVariation(null);
    }
  };

  const handleVariationConfirm = async (product) => {
    if (!accordionVariation) return;
    try {
      setConfirming(true);
      await addSizesToProducts(product, accordionVariation);
    } finally {
      setConfirming(false);
      setExpandedProductId(null);
      setAccordionVariation(null);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (formData) {
      setSearchText(formData.productStyle || null);
    }
  }, [formData]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getSizesData();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchText && isShowModal) {
        getProductsData();
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText, isShowModal]);

  return (
    <div className="container-fluid p-0">
      <div className="row mb-3">
        <div className="col-12">
          <div className="input-group">
            <div className="input-group-prepend ">
              <span className="input-group-text fs-3 p-0 px-2 ">
                <i className="mdi mdi-database-search"></i>
              </span>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Style ID..."
              value={searchText || ""}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <div className="product-list">
          {productsData && productsData.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="thead-light">
                  <tr>
                    <th>Product Type</th>
                    <th>Style ID</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(expandedProductId
                    ? productsData.filter((p) => p.id === expandedProductId)
                    : productsData
                  ).map((product) => {
                    const isExpanded = expandedProductId === product.id;
                    const variations = parseVariations(product.variations);
                    const selectedKey = accordionVariation
                      ? accordionVariation.variationName ||
                        accordionVariation.color
                      : "";
                    return (
                      <>
                        <tr
                          key={product.id}
                          className={isExpanded ? "table-active" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleAccordion(product.id)}
                        >
                          <td className="py-1 align-middle">
                            {product.productType?.productTypes || "N/A"}
                          </td>
                          <td className="py-1 align-middle">
                            {product.styleId}
                          </td>
                          <td className="py-1 align-middle">{product.desc}</td>
                          <td className="py-1 align-middle">
                            <button
                              className={`btn btn-sm ${
                                isExpanded
                                  ? "btn-warning"
                                  : "btn-outline-primary"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleAccordion(product.id);
                              }}
                            >
                              <i
                                className={`mdi ${
                                  isExpanded
                                    ? "mdi-chevron-up"
                                    : "mdi-chevron-down"
                                }`}
                              ></i>{" "}
                              {isExpanded ? "Close" : "Select"}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${product.id}-accordion`}>
                            <td
                              colSpan={4}
                              className="p-2 bg-light border-top-0"
                            >
                              {variations.length === 0 ? (
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    className="text-muted"
                                    style={{ fontSize: 13 }}
                                  >
                                    No color variations for this product.
                                  </span>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={confirming}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirming(true);
                                      addSizesToProducts(
                                        product,
                                        null,
                                        true,
                                      ).finally(() => {
                                        setConfirming(false);
                                        setExpandedProductId(null);
                                      });
                                    }}
                                  >
                                    {confirming ? (
                                      <>
                                        <span
                                          className="spinner-border spinner-border-sm me-1"
                                          role="status"
                                        />
                                        Processing...
                                      </>
                                    ) : (
                                      "Confirm"
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <select
                                    className="form-select form-select-sm"
                                    value={selectedKey}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const found = variations.find(
                                        (v) =>
                                          (v.variationName || v.color) === val,
                                      );
                                      setAccordionVariation(found || null);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="">-- Select color --</option>
                                    {variations.map((v) => {
                                      const key = v.variationName || v.color;
                                      const hasImage =
                                        Array.isArray(v.images) &&
                                        v.images.length > 0;
                                      return (
                                        <option key={key} value={key}>
                                          {v.color}
                                          {hasImage ? " 🖼" : ""}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={!accordionVariation || confirming}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleVariationConfirm(product);
                                    }}
                                  >
                                    {confirming ? (
                                      <>
                                        <span
                                          className="spinner-border spinner-border-sm me-1"
                                          role="status"
                                        />
                                        Processing...
                                      </>
                                    ) : (
                                      "Confirm"
                                    )}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="alert alert-info text-center" role="alert">
              {searchText
                ? "No products found matching your search."
                : "Enter a Style ID to search for products."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
